import { useCallback, useContext, useMemo } from 'react';
import {
  useAutomationsDispatch,
  useAutomationsSelector,
} from 'pages/AutomationEngine/store/react';
import { actions } from 'pages/AutomationEngine/store';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import type { ExecutionHistory, Step } from 'pages/AutomationEngine/types';
import { getSteps, getTriggers } from '../store/selectors';

export type HistoryWithStep = ExecutionHistory & { step: Step | undefined };

export const useHistoryNavigation = () => {
  const dispatch = useAutomationsDispatch();
  const steps: Step[] = useAutomationsSelector(getSteps);
  const triggers: Step[] = useAutomationsSelector(getTriggers);

  const {
    setSelectedHistoryStep,
    selectedHistoryStep,
    orderedExecutionHistory,
  } = useContext(ExecutionHistoryContext);

  const orderedExecutionHistoryWithStep: HistoryWithStep[] = useMemo(
    () =>
      orderedExecutionHistory.map((history) => {
        let step: Step | undefined;
        if (history.stepId) {
          step =
            steps.find((step: Step) => step.id === history.stepId) ||
            history.step;
          return {
            ...history,
            step: step,
          };
        }
        return {
          ...history,
          step:
            triggers.find(
              (trigger: Step) => trigger.id === history.triggerId
            ) || history.trigger,
        };
      }),
    [orderedExecutionHistory, steps, triggers]
  );

  const handleClick = useCallback(
    (index: number) => {
      setSelectedHistoryStep(orderedExecutionHistoryWithStep[index]);
      const selectedHistoryStep = orderedExecutionHistoryWithStep[index];
      dispatch(
        actions.setFocusStep({
          step: selectedHistoryStep.stepId || selectedHistoryStep.triggerId,
        })
      );
    },
    [setSelectedHistoryStep, orderedExecutionHistoryWithStep, dispatch]
  );

  const clickUp = useCallback(() => {
    const selectedHistoryStepIndex = orderedExecutionHistoryWithStep.findIndex(
      (history) => history.id === selectedHistoryStep?.id
    );
    if (selectedHistoryStepIndex > 0) {
      handleClick(selectedHistoryStepIndex - 1);
    }
  }, [selectedHistoryStep?.id, orderedExecutionHistoryWithStep, handleClick]);

  const clickDown = useCallback(() => {
    const selectedHistoryStepIndex = orderedExecutionHistoryWithStep.findIndex(
      (history) => history.id === selectedHistoryStep?.id
    );
    if (selectedHistoryStepIndex < orderedExecutionHistoryWithStep.length - 1) {
      handleClick(selectedHistoryStepIndex + 1);
    }
  }, [selectedHistoryStep?.id, orderedExecutionHistoryWithStep, handleClick]);

  return { handleClick, clickUp, clickDown, orderedExecutionHistoryWithStep };
};
